import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { TemplateListParams } from '../_types';
import datetimeHelper from 'helpers/datetimeHelper';

interface TemplateFiltersGroupProps {
  params: TemplateListParams;
  updateParams: (updatedParams: TemplateListParams) => void;
  clearParams: () => void;
}

const TemplateFiltersGroup: React.FC<TemplateFiltersGroupProps> = ({ params, updateParams, clearParams }) => {
  const activeFilters = useMemo(() => {
    const filters: Array<{
      key: string;
      label: string;
      value: string | number;
      removeAction: () => void;
    }> = [];

    // Search filter
    if (params.search) {
      filters.push({
        key: 'search',
        label: t('performanceReporting.templates.list.filters.search') || 'Search',
        value: params.search,
        removeAction: () => updateParams({ search: null }),
      });
    }

    // Status filter
    if (params.status) {
      filters.push({
        key: 'status',
        label: t('performanceReporting.templates.list.filters.status') || 'Status',
        value: t(`performanceReporting.templates.list.status.${params.status}`) || params.status,
        removeAction: () => updateParams({ status: null }),
      });
    }

    // Effective date from filter
    if (params.effectiveDateFrom) {
      filters.push({
        key: 'effectiveDateFrom',
        label: t('performanceReporting.templates.list.filters.effectiveDateFrom') || 'Effective Date From',
        value: datetimeHelper.formatDate(params.effectiveDateFrom, { format: 'short' }),
        removeAction: () => updateParams({ effectiveDateFrom: null }),
      });
    }

    // Effective date to filter
    if (params.effectiveDateTo) {
      filters.push({
        key: 'effectiveDateTo',
        label: t('performanceReporting.templates.list.filters.effectiveDateTo') || 'Effective Date To',
        value: datetimeHelper.formatDate(params.effectiveDateTo, { format: 'short' }),
        removeAction: () => updateParams({ effectiveDateTo: null }),
      });
    }

    // Last updated from filter
    if (params.lastUpdatedFrom) {
      filters.push({
        key: 'lastUpdatedFrom',
        label: t('performanceReporting.templates.list.filters.lastUpdatedFrom'),
        value: datetimeHelper.formatDate(params.lastUpdatedFrom, { format: 'short' }),
        removeAction: () => updateParams({ lastUpdatedFrom: null }),
      });
    }

    // Last updated to filter
    if (params.lastUpdatedTo) {
      filters.push({
        key: 'lastUpdatedTo',
        label: t('performanceReporting.templates.list.filters.lastUpdatedTo'),
        value: datetimeHelper.formatDate(params.lastUpdatedTo, { format: 'short' }),
        removeAction: () => updateParams({ lastUpdatedTo: null }),
      });
    }

    // Authors filters (array)
    if (params.authors && params.authors.length > 0) {
      params.authors.forEach((author) => {
        filters.push({
          key: `author-${author}`,
          label: t('performanceReporting.templates.list.filters.authors') || 'Author',
          value: author,
          removeAction: () => {
            const updatedAuthors = params.authors?.filter((a) => a !== author) || [];
            updateParams({
              authors: updatedAuthors.length > 0 ? updatedAuthors : null,
            });
          },
        });
      });
    }

    return filters;
  }, [params, updateParams]);

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box>
      <Stack
        direction='row'
        spacing={3}
        flexWrap='wrap'
        useFlexGap
        alignItems='center'
      >
        {activeFilters.map((filter) => (
          <Chip
            key={filter.key}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span className='filter-chip-label'>{filter.label}:</span>
                <span>{filter.value}</span>
              </Box>
            }
            onDelete={filter.removeAction}
            deleteIcon={<RemoveIcon />}
            size='small'
            className='filter-chip'
          />
        ))}
        {activeFilters.length > 0 && (
          <Typography
            variant='linkSm'
            component='a'
            onClick={clearParams}
            sx={{
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
              '&:active': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('common.clearAll')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default TemplateFiltersGroup;
